// pp-calc-ubicaciones.js — Generador de Etiquetas de Ubicación en Serie
// (/pages/etiquetas-de-ubicacion-para-imprimir)
//
// Rotular un rack completo de un jalón: el almacén escribe el prefijo del
// pasillo ("A"), el rango (1 a 20) y sale la serie completa — A-01, A-02…
// A-20 — como PDF multi-página listo para la impresora térmica (Dymo, 4×6",
// 4×2", 3×2", 2×1"), o en hoja carta a 6/10/14/30 por hoja para recortar
// (14 y 30 con la geometría de las planillas tipo Avery 5162/5160).
//
// Lógica pura (serie + paginado + PDF) en PPUbicaciones, probada en
// tests/calc-ubicaciones.test.mjs. El wiring de DOM va aparte, protegido
// con typeof document.
//
// SEGURIDAD: el prefijo es entrada del usuario. Viaja como DATO por esta
// capa y se pinta ÚNICAMENTE con textContent en el wiring (y con fillText
// en canvas, donde no hay markup que interpretar) — jamás innerHTML.
(function (root) {
  'use strict';

  // Tope por corrida: el PDF se arma entero en el navegador (una página
  // rasterizada a 300 dpi por etiqueta térmica); 200 es lo que cualquier
  // equipo genera sin trabarse. Más que eso → error explicable, no un
  // navegador congelado.
  var TOPE = 200;

  // El prefijo NO se transforma ni se interpreta: se recorta (espacios y
  // largo máximo de 6) y ya. La capa de DOM lo asigna con textContent.
  function limpiar(t) {
    if (t === null || t === undefined) return '';
    return String(t).trim();
  }

  // El select manda nombres ('guion' / 'punto' / 'nada'); la función también
  // acepta el literal por si alguien la llama directo. Default: guion.
  function separadorReal(s) {
    if (s === 'punto' || s === '.') return '.';
    if (s === 'nada' || s === '') return '';
    return '-';
  }

  function entero(v, porDefecto) {
    if (v === null || v === undefined || v === '') return porDefecto;
    var n = parseInt(v, 10);
    return isFinite(n) ? n : porDefecto;
  }

  // Entradas: { prefijo, separador, inicio, fin, digitos } →
  //   { ok: true, serie: ['A-01', … 'A-20'], prefijo, separador }
  //   { ok: false, error: 'mensaje explicable' }
  // digitos: 2 → "01" (default), 3 → "001", 0 → sin ceros. Un número que ya
  // rebasa los dígitos no se recorta (A-100 con 2 dígitos sale A-100).
  function generarSerie(e) {
    e = e || {};
    var prefijo = limpiar(e.prefijo).slice(0, 6);
    var sep = separadorReal(e.separador);
    var inicio = entero(e.inicio, 1);
    var fin = entero(e.fin, 20);
    var d = entero(e.digitos, 2);
    if (d !== 0 && d !== 2 && d !== 3) d = 2;
    if (inicio < 0) {
      return { ok: false, error: 'El número inicial no puede ser negativo.' };
    }
    if (inicio > fin) {
      return { ok: false, error: 'El número inicial (' + inicio + ') debe ser menor o igual que el final (' + fin + ').' };
    }
    var n = fin - inicio + 1;
    if (n > TOPE) {
      var corte = inicio + TOPE - 1;
      return {
        ok: false,
        error: 'Esa serie son ' + n + ' etiquetas y el tope es ' + TOPE + ' por corrida — ' +
          'para más de 200, hazlo en dos corridas: primero ' + inicio + ' a ' + corte +
          ' y luego ' + (corte + 1) + ' a ' + fin + '.'
      };
    }
    var serie = [];
    for (var i = inicio; i <= fin; i++) {
      var num = d > 0 ? String(i).padStart(d, '0') : String(i);
      serie.push(prefijo ? prefijo + sep + num : num);
    }
    return { ok: true, serie: serie, prefijo: prefijo, separador: sep };
  }

  // Formatos de salida. Las térmicas son etiquetas reales de almacén (4×2"
  // es LA clásica de racks; 2×1" la chica de bins): una ubicación por
  // etiqueta. Las cartas son el plan B sin impresora de etiquetas: rejilla
  // para recortar y pegar con cinta ancha encima — y en 14 y 30 por hoja la
  // rejilla trae la geometría EXACTA de las planillas de etiquetas tipo
  // Avery 5162/5160, así que también sirven impresas directo sobre la hoja
  // de etiquetas.
  //
  // En toda rejilla porPagina = cols × filas. `rejilla` (en puntos, 1 in =
  // 72 pt) es la geometría del troquel: etW/etH la etiqueta, izq/sup el
  // margen hasta la primera, pasoX/pasoY el paso entre etiquetas (el paso
  // menos la etiqueta es la calle). Sin `rejilla`, la hoja se reparte
  // uniforme con margen de 10 mm (el formato "para recortar" clásico).
  var FORMATOS = {
    dymo2x4: { porPagina: 1, etiqueta: true, pagina: '2.125in 4in' },
    e4x6: { porPagina: 1, etiqueta: true, pagina: '4in 6in' },
    t4x2: { porPagina: 1, etiqueta: true, pagina: '4in 2in' },
    t3x2: { porPagina: 1, etiqueta: true, pagina: '3in 2in' },
    t2x1: { porPagina: 1, etiqueta: true, pagina: '2in 1in' },
    carta10: { porPagina: 10, etiqueta: false, pagina: 'letter portrait', cols: 2, filas: 5 },
    carta14: {
      porPagina: 14, etiqueta: false, pagina: 'letter portrait', cols: 2, filas: 7,
      // Planilla tipo Avery 5162: etiqueta de 4 × 1 1/3 in; margen izquierdo
      // 0.15625 in, superior 5/6 in; paso 4.1875 × 1 1/3 in.
      rejilla: { etW: 288, etH: 96, izq: 11.25, sup: 60, pasoX: 301.5, pasoY: 96 }
    },
    carta30: {
      porPagina: 30, etiqueta: false, pagina: 'letter portrait', cols: 3, filas: 10,
      // Planilla tipo Avery 5160: etiqueta de 2 5/8 × 1 in; margen izquierdo
      // 0.1875 in, superior 0.5 in; paso 2.75 × 1 in. El clásico.
      rejilla: { etW: 189, etH: 72, izq: 13.5, sup: 36, pasoX: 198, pasoY: 72 }
    },
    carta6: { porPagina: 6, etiqueta: false, pagina: 'letter portrait', cols: 2, filas: 3 }
  };

  // Tamaño de página en puntos (1 in = 72 pt) por formato.
  var PUNTOS = {
    dymo2x4: { w: 153, h: 288 },
    e4x6: { w: 288, h: 432 },
    t4x2: { w: 288, h: 144 },
    t3x2: { w: 216, h: 144 },
    t2x1: { w: 144, h: 72 },
    carta10: { w: 612, h: 792 },
    carta14: { w: 612, h: 792 },
    carta30: { w: 612, h: 792 },
    carta6: { w: 612, h: 792 }
  };

  // serie → arreglo de páginas, cada una con sus etiquetas:
  // [['A-01'], ['A-02'], …] en térmicas; [['A-01', … 'A-10'], …] en carta.
  function paginar(serie, formato) {
    var f = FORMATOS[formato] || FORMATOS.dymo2x4;
    var paginas = [];
    for (var i = 0; i < serie.length; i += f.porPagina) {
      paginas.push(serie.slice(i, i + f.porPagina));
    }
    return paginas;
  }

  // El string del @page para "o imprimir directo". Margin 0 SIEMPRE: sin
  // margen, Chrome no tiene dónde pintar su fecha y su URL (la lección del
  // QA del generador de rótulos, 1-sep).
  function paginaCss(formato) {
    var f = FORMATOS[formato] || FORMATOS.dymo2x4;
    return '@page { size: ' + f.pagina + '; margin: 0; }';
  }

  // ── PDF multi-página hecho en casa ────────────────────────────────────
  // Generalización del pdfConImagen de pp-calc-rotulos.js (PDF 1.4 de una
  // página) a N páginas del MISMO tamaño. paginas = [{ jpeg: Uint8Array,
  // wPx, hPx }]; wPt/hPt: la página en puntos.
  //
  // Numeración de objetos: 1 = Catalog, 2 = Pages (Kids con N referencias,
  // Count N); por página i (base 0): 3+3i = Page, 4+3i = imagen XObject,
  // 5+3i = content stream. Total 2 + 3N objetos, todos en el xref con su
  // offset exacto. Puro: probado en node con jpegs sintéticos.
  function pdfMultipagina(paginas, wPt, hPt) {
    if (!paginas || !paginas.length) return null;
    var enc = new TextEncoder();
    var n = paginas.length;
    var totalObjs = 2 + 3 * n;
    var partes = [];
    var offset = 0;
    var offsets = [];
    function put(bytes) { partes.push(bytes); offset += bytes.length; }
    function obj(num, cuerpo) {
      offsets[num - 1] = offset;
      put(enc.encode(num + ' 0 obj\n' + cuerpo + '\nendobj\n'));
    }
    put(enc.encode('%PDF-1.4\n'));
    obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
    var kids = [];
    for (var k = 0; k < n; k++) kids.push((3 + 3 * k) + ' 0 R');
    obj(2, '<< /Type /Pages /Kids [' + kids.join(' ') + '] /Count ' + n + ' >>');
    for (var i = 0; i < n; i++) {
      var p = paginas[i];
      var nPag = 3 + 3 * i;
      var nImg = 4 + 3 * i;
      var nCont = 5 + 3 * i;
      var contenido = 'q ' + wPt + ' 0 0 ' + hPt + ' 0 0 cm /Im1 Do Q';
      obj(nPag, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + wPt + ' ' + hPt +
        '] /Resources << /XObject << /Im1 ' + nImg + ' 0 R >> >> /Contents ' + nCont + ' 0 R >>');
      // La imagen va a mano por el stream binario del JPEG.
      offsets[nImg - 1] = offset;
      put(enc.encode(nImg + ' 0 obj\n<< /Type /XObject /Subtype /Image /Width ' + p.wPx +
        ' /Height ' + p.hPx + ' /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ' +
        p.jpeg.length + ' >>\nstream\n'));
      put(p.jpeg);
      put(enc.encode('\nendstream\nendobj\n'));
      obj(nCont, '<< /Length ' + contenido.length + ' >>\nstream\n' + contenido + '\nendstream');
    }
    var xrefPos = offset;
    var xref = 'xref\n0 ' + (totalObjs + 1) + '\n0000000000 65535 f \n';
    for (var j = 0; j < totalObjs; j++) {
      xref += String(offsets[j]).padStart(10, '0') + ' 00000 n \n';
    }
    put(enc.encode(xref + 'trailer\n<< /Size ' + (totalObjs + 1) + ' /Root 1 0 R >>\nstartxref\n' + xrefPos + '\n%%EOF'));
    var total = new Uint8Array(offset);
    var pos = 0;
    for (var t = 0; t < partes.length; t++) { total.set(partes[t], pos); pos += partes[t].length; }
    return total;
  }

  // Nombre de archivo amable: "Etiquetas A-01 a A-20 - Para Paquetes.pdf".
  // Acentos fuera y solo caracteres seguros (el punto y el guion de los
  // códigos sobreviven).
  function limpiaCodigo(c) {
    return String(c).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9.-]/g, '').slice(0, 12);
  }
  function nombreArchivo(primera, ultima) {
    return 'Etiquetas ' + limpiaCodigo(primera) + ' a ' + limpiaCodigo(ultima) + ' - Para Paquetes.pdf';
  }

  root.PPUbicaciones = {
    TOPE: TOPE,
    FORMATOS: FORMATOS,
    PUNTOS: PUNTOS,
    generarSerie: generarSerie,
    paginar: paginar,
    paginaCss: paginaCss,
    pdfMultipagina: pdfMultipagina,
    nombreArchivo: nombreArchivo
  };
})(typeof window !== 'undefined' ? window : globalThis);

// ── Wiring de la página (no corre en tests de node) ────────────────────────
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  var PIE = 'parapaquetes.com';
  var MUESTRA = 10; // etiquetas en la vista previa

  function listo(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  listo(function () {
    var raiz = document.querySelector('[data-pp-calc-ubicaciones]');
    if (!raiz) return;
    var U = window.PPUbicaciones;
    var form = raiz.querySelector('form');
    var previewEl = raiz.querySelector('[data-ubic-preview]');
    var resumenEl = raiz.querySelector('[data-ubic-resumen]');
    var errorEl = raiz.querySelector('[data-ubic-error]');
    var impresionEl = raiz.querySelector('[data-ubic-impresion]');
    var botonDescargar = raiz.querySelector('[data-descargar]');
    var botonImprimir = raiz.querySelector('[data-imprimir]');
    if (!form || !previewEl) return;

    // @page no puede depender de una clase: este <style> propio se
    // reescribe al vuelo con el formato elegido.
    var estiloPagina = document.createElement('style');
    document.head.appendChild(estiloPagina);

    // dymo2x4 / e4x6 / carta10 tienen sus reglas a mano en
    // pp-calc-ubicaciones.css. Las de los formatos NUEVOS (proporción real
    // en la vista previa, tipografía por alto en etiquetas apaisadas,
    // rejilla del "imprimir directo") se GENERAN desde FORMATOS/PUNTOS:
    // una sola fuente de verdad, cero drift entre el mapa y el CSS. Todos
    // los selectores llevan [data-formato], más específicos que los base —
    // ganan sin depender del orden de las hojas.
    var EN_CSS = { dymo2x4: true, e4x6: true, carta10: true };
    function cssDeFormatos() {
      var css = '';
      var MARGEN_PT = 28.35; // 10 mm, el margen de la rejilla uniforme
      Object.keys(U.FORMATOS).forEach(function (k) {
        if (EN_CSS[k]) return;
        var f = U.FORMATOS[k];
        var pts = U.PUNTOS[k];
        var celW, celH;
        if (f.etiqueta) { celW = pts.w; celH = pts.h; }
        else if (f.rejilla) { celW = f.rejilla.etW; celH = f.rejilla.etH; }
        else { celW = (pts.w - 2 * MARGEN_PT) / f.cols; celH = (pts.h - 2 * MARGEN_PT) / f.filas; }
        // La proporción real de cada formato: la vista previa no miente.
        css += ".pp-ubic__preview[data-formato='" + k + "'] .pp-ubic__etiqueta { aspect-ratio: " + celW + ' / ' + celH + '; }\n';
        if (celW > celH) {
          // Etiqueta apaisada: la tipografía manda por ALTO (cqh), el
          // mismo patrón que carta10 en el CSS.
          [['', 34], ["[data-escala='media']", 26], ["[data-escala='chica']", 20]].forEach(function (par) {
            css += ".pp-ubic__preview[data-formato='" + k + "'] .pp-ubic__etiqueta" + par[0] + ' .pp-ubic__codigo, ' +
              ".pp-ubic-hoja[data-formato='" + k + "'] .pp-ubic__etiqueta" + par[0] + ' .pp-ubic__codigo { font-size: ' + par[1] + 'cqh; }\n';
          });
        }
        if (!f.etiqueta && f.rejilla) {
          // La hoja impresa directo replica el troquel de la planilla:
          // margen y paso exactos, calles como gap.
          var g = f.rejilla;
          css += ".pp-ubic-hoja[data-formato='" + k + "'] { padding: " + g.sup + 'pt ' + g.izq + 'pt; ' +
            'grid-template-columns: repeat(' + f.cols + ', ' + g.etW + 'pt); ' +
            'grid-template-rows: repeat(' + f.filas + ', ' + g.etH + 'pt); ' +
            'column-gap: ' + (g.pasoX - g.etW) + 'pt; row-gap: ' + (g.pasoY - g.etH) + 'pt; ' +
            'align-content: start; justify-content: start; }\n';
          css += ".pp-ubic-hoja[data-formato='" + k + "'] .pp-ubic__celda { border: 1px dashed #9ca3af; padding: 1.5mm; }\n";
        } else if (!f.etiqueta) {
          css += ".pp-ubic-hoja[data-formato='" + k + "'] { padding: 10mm; " +
            'grid-template-columns: repeat(' + f.cols + ', 1fr); grid-template-rows: repeat(' + f.filas + ', 1fr); }\n';
          css += ".pp-ubic-hoja[data-formato='" + k + "'] .pp-ubic__celda { border: 1px dashed #9ca3af; padding: 3mm; }\n";
        }
      });
      return css;
    }
    var estiloFormatos = document.createElement('style');
    estiloFormatos.textContent = cssDeFormatos();
    document.head.appendChild(estiloFormatos);

    function entrada() {
      var f = new FormData(form);
      return {
        prefijo: f.get('prefijo'),
        separador: f.get('separador'),
        inicio: f.get('inicio'),
        fin: f.get('fin'),
        digitos: f.get('digitos'),
        formato: U.FORMATOS[f.get('formato')] ? f.get('formato') : 'dymo2x4'
      };
    }

    // Una etiqueta de la serie, como nodo DOM. SEGURIDAD: el código lleva
    // el prefijo del usuario y se asigna SOLO con textContent.
    function crearEtiqueta(codigo) {
      var et = document.createElement('div');
      et.className = 'pp-ubic__etiqueta';
      // Escala por largo: un código de 4 caracteres se pinta enorme, uno de
      // 10 se encoge en vez de desbordarse (el CSS pone el tamaño).
      et.dataset.escala = codigo.length <= 5 ? 'grande' : (codigo.length <= 8 ? 'media' : 'chica');
      var cod = document.createElement('span');
      cod.className = 'pp-ubic__codigo';
      cod.textContent = codigo;
      et.appendChild(cod);
      var pie = document.createElement('span');
      pie.className = 'pp-ubic__pie';
      pie.textContent = PIE;
      et.appendChild(pie);
      return et;
    }

    function pintarError(mensaje) {
      if (errorEl) {
        errorEl.textContent = mensaje;
        errorEl.hidden = false;
      }
      previewEl.textContent = '';
      if (resumenEl) resumenEl.textContent = '';
      if (botonDescargar) botonDescargar.disabled = true;
      if (botonImprimir) botonImprimir.disabled = true;
    }

    function pintarPreview(serie, formato) {
      if (errorEl) errorEl.hidden = true;
      if (botonDescargar) botonDescargar.disabled = false;
      if (botonImprimir) botonImprimir.disabled = false;
      previewEl.dataset.formato = formato;
      previewEl.textContent = '';
      var muestra = serie.slice(0, MUESTRA);
      for (var i = 0; i < muestra.length; i++) {
        previewEl.appendChild(crearEtiqueta(muestra[i]));
      }
      if (serie.length > muestra.length) {
        var mas = document.createElement('p');
        mas.className = 'pp-ubic__mas';
        mas.textContent = '… y ' + (serie.length - muestra.length) + ' más';
        previewEl.appendChild(mas);
      }
      if (resumenEl) {
        var paginas = U.paginar(serie, formato).length;
        var f = U.FORMATOS[formato];
        resumenEl.textContent = serie.length + (serie.length === 1 ? ' etiqueta' : ' etiquetas') +
          ' · ' + paginas + (paginas === 1 ? (f.etiqueta ? ' página' : ' hoja') : (f.etiqueta ? ' páginas' : ' hojas'));
      }
    }

    function render() {
      var e = entrada();
      estiloPagina.textContent = U.paginaCss(e.formato);
      var res = U.generarSerie(e);
      if (!res.ok) {
        pintarError(res.error);
        return;
      }
      pintarPreview(res.serie, e.formato);
    }

    form.addEventListener('input', render);
    form.addEventListener('change', render);
    form.addEventListener('submit', function (ev) { ev.preventDefault(); });

    // ── Impresión directa ────────────────────────────────────────────────
    // La serie completa se arma como hojas (una por página) en el
    // contenedor de impresión, que se MUEVE a hijo directo de <body>
    // mientras se imprime: el documento impreso es SOLO la serie (patrón
    // del generador de rótulos — los elementos invisibles siguen ocupando
    // altura si se quedan dentro del layout).
    var marcador = document.createComment('pp-ubic-impresion-va-aqui');
    var tituloOriginal = document.title;
    function alImprimir() {
      if (!impresionEl || impresionEl.parentNode === document.body) return;
      var e = entrada();
      var res = U.generarSerie(e);
      if (!res.ok) return;
      impresionEl.textContent = '';
      impresionEl.dataset.formato = e.formato;
      var paginas = U.paginar(res.serie, e.formato);
      for (var i = 0; i < paginas.length; i++) {
        var hoja = document.createElement('div');
        hoja.className = 'pp-ubic-hoja';
        hoja.dataset.formato = e.formato;
        for (var j = 0; j < paginas[i].length; j++) {
          var celda = document.createElement('div');
          celda.className = 'pp-ubic__celda';
          celda.appendChild(crearEtiqueta(paginas[i][j]));
          hoja.appendChild(celda);
        }
        impresionEl.appendChild(hoja);
      }
      impresionEl.parentNode.insertBefore(marcador, impresionEl);
      document.body.appendChild(impresionEl);
      document.body.classList.add('pp-imprimiendo-ubic');
      // El @page nuestro se muda al FINAL del documento: los CSS de apps se
      // inyectan después del nuestro y un @page ajeno le ganaba por orden.
      document.body.appendChild(estiloPagina);
      document.title = 'Etiquetas ' + res.serie[0] + ' a ' + res.serie[res.serie.length - 1] + ' — Para Paquetes';
    }
    function alTerminar() {
      if (marcador.parentNode) {
        marcador.parentNode.insertBefore(impresionEl, marcador);
        marcador.parentNode.removeChild(marcador);
      }
      if (impresionEl) impresionEl.textContent = '';
      document.body.classList.remove('pp-imprimiendo-ubic');
      document.title = tituloOriginal;
    }
    window.addEventListener('beforeprint', alImprimir);
    window.addEventListener('afterprint', alTerminar);

    if (botonImprimir) {
      botonImprimir.addEventListener('click', function () { window.print(); });
    }

    // ── Descargar PDF (el camino recomendado) ────────────────────────────
    // Cada página se rasteriza a 300 dpi en UN canvas que se reusa (las
    // páginas se generan en serie: nunca 200 canvas vivos a la vez) y todo
    // se empaca en PPUbicaciones.pdfMultipagina — páginas del tamaño EXACTO
    // del formato. Del PDF descargado, cualquier impresora sale bien.
    var DPI = 300;
    var NAVY = '#0E3A5C';
    var GRIS = '#6b7280';

    function fuente(peso, tam) {
      return peso + ' ' + tam + 'px "Inter Tight", -apple-system, sans-serif';
    }

    // Una etiqueta en el canvas: borde delgado, código GRANDE al centro
    // (~40% del alto, encogido con measureText si no cabe), pie diminuto.
    function dibujarEtiqueta(ctx, x, y, w, h, codigo) {
      var borde = Math.max(2, Math.round(Math.min(w, h) * 0.012));
      ctx.strokeStyle = NAVY;
      ctx.lineWidth = borde;
      ctx.strokeRect(x + borde / 2, y + borde / 2, w - borde, h - borde);
      var relleno = Math.round(Math.min(w, h) * 0.06);
      var interiorW = w - 2 * (borde + relleno);
      var tam = Math.round(h * 0.4);
      ctx.font = fuente(800, tam);
      while (tam > 10 && ctx.measureText(codigo).width > interiorW) {
        tam = Math.round(tam * 0.94);
        ctx.font = fuente(800, tam);
      }
      ctx.fillStyle = NAVY;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(codigo, x + w / 2, y + h * 0.47, interiorW);
      // El pie escala con la etiqueta, con piso legible: en las chicas
      // (2 × 1", carta a 30) el 2.2% del alto salía microscópico — sube
      // hasta ~6 pt (24 px a 300 dpi) mientras quepa en el 9% del alto.
      var tamPie = Math.max(8, Math.round(Math.max(h * 0.022, Math.min(24, h * 0.09))));
      ctx.font = fuente(400, tamPie);
      while (tamPie > 6 && ctx.measureText(PIE).width > interiorW) {
        tamPie -= 1;
        ctx.font = fuente(400, tamPie);
      }
      ctx.fillStyle = GRIS;
      ctx.textBaseline = 'bottom';
      ctx.fillText(PIE, x + w / 2, y + h - borde - relleno * 0.5, interiorW);
    }

    // Rasteriza UNA página (reusando el canvas que le pasen) y regresa sus
    // pixeles. Térmica: la etiqueta llena la página (margen de 2mm). Carta
    // con `rejilla`: cada etiqueta en la posición EXACTA del troquel de la
    // planilla (tipo Avery). Carta sin `rejilla`: reparto uniforme
    // cols × filas con líneas punteadas de corte.
    function rasterizarPagina(canvas, etiquetas, formato) {
      var pts = U.PUNTOS[formato] || U.PUNTOS.dymo2x4;
      var esc = DPI / 72;
      canvas.width = Math.round(pts.w * esc);
      canvas.height = Math.round(pts.h * esc);
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      var f = U.FORMATOS[formato] || U.FORMATOS.dymo2x4;
      if (f.porPagina === 1) {
        var m = Math.round(2 / 25.4 * DPI); // 2 mm
        dibujarEtiqueta(ctx, m, m, canvas.width - 2 * m, canvas.height - 2 * m, etiquetas[0]);
      } else if (f.rejilla) {
        // Geometría de planilla: el troquel completo en punteado (aunque la
        // hoja venga parcial — impreso sobre la hoja de etiquetas la línea
        // cae en el borde troquelado; recortado, es la guía de tijera) y la
        // etiqueta con 1.5 mm de aire adentro.
        var g = f.rejilla;
        ctx.save();
        ctx.strokeStyle = '#9ca3af';
        ctx.lineWidth = 2;
        ctx.setLineDash([14, 10]);
        for (var a = 0; a < f.porPagina; a++) {
          var colA = a % f.cols;
          var filaA = (a - colA) / f.cols;
          ctx.strokeRect(Math.round((g.izq + colA * g.pasoX) * esc), Math.round((g.sup + filaA * g.pasoY) * esc),
            Math.round(g.etW * esc), Math.round(g.etH * esc));
        }
        ctx.restore();
        var aire = Math.round(1.5 / 25.4 * DPI); // 1.5 mm
        for (var b = 0; b < etiquetas.length && b < f.porPagina; b++) {
          var colB = b % f.cols;
          var filaB = (b - colB) / f.cols;
          dibujarEtiqueta(ctx,
            Math.round((g.izq + colB * g.pasoX) * esc) + aire,
            Math.round((g.sup + filaB * g.pasoY) * esc) + aire,
            Math.round(g.etW * esc) - 2 * aire,
            Math.round(g.etH * esc) - 2 * aire,
            etiquetas[b]);
        }
      } else {
        var margen = Math.round(10 / 25.4 * DPI); // 10 mm
        var utilW = canvas.width - 2 * margen;
        var utilH = canvas.height - 2 * margen;
        var cw = utilW / f.cols;
        var ch = utilH / f.filas;
        // Líneas punteadas de corte: la rejilla completa, aunque la última
        // hoja venga parcial — recortar parejo es más fácil.
        ctx.save();
        ctx.strokeStyle = '#9ca3af';
        ctx.lineWidth = 2;
        ctx.setLineDash([14, 10]);
        ctx.beginPath();
        for (var c = 0; c <= f.cols; c++) {
          ctx.moveTo(margen + c * cw, margen);
          ctx.lineTo(margen + c * cw, margen + utilH);
        }
        for (var r = 0; r <= f.filas; r++) {
          ctx.moveTo(margen, margen + r * ch);
          ctx.lineTo(margen + utilW, margen + r * ch);
        }
        ctx.stroke();
        ctx.restore();
        var inset = Math.round(3 / 25.4 * DPI); // 3 mm entre corte y borde
        for (var i = 0; i < etiquetas.length && i < f.porPagina; i++) {
          var col = i % f.cols;
          var fila = (i - col) / f.cols;
          dibujarEtiqueta(ctx, margen + col * cw + inset, margen + fila * ch + inset,
            cw - 2 * inset, ch - 2 * inset, etiquetas[i]);
        }
      }
      return { wPx: canvas.width, hPx: canvas.height };
    }

    function jpegDeCanvas(canvas) {
      var b64 = canvas.toDataURL('image/jpeg', 0.92).split(',')[1];
      var bin = atob(b64);
      var jpeg = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) jpeg[i] = bin.charCodeAt(i);
      return jpeg;
    }

    function descargar() {
      var e = entrada();
      var res = U.generarSerie(e);
      if (!res.ok) return;
      var listoFuentes = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
      listoFuentes
        .then(function () {
          var paginas = U.paginar(res.serie, e.formato);
          var pts = U.PUNTOS[e.formato];
          var canvas = document.createElement('canvas'); // UNO, reusado página a página
          var imagenes = [];
          for (var i = 0; i < paginas.length; i++) {
            var dim = rasterizarPagina(canvas, paginas[i], e.formato);
            imagenes.push({ jpeg: jpegDeCanvas(canvas), wPx: dim.wPx, hPx: dim.hPx });
          }
          var pdf = U.pdfMultipagina(imagenes, pts.w, pts.h);
          if (!pdf) return;
          var blob = new Blob([pdf], { type: 'application/pdf' });
          var a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = U.nombreArchivo(res.serie[0], res.serie[res.serie.length - 1]);
          document.body.appendChild(a);
          a.click();
          setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 2000);
        })
        .catch(function () { window.print(); }); // si algo truena, el plan B es imprimir
    }

    if (botonDescargar) {
      botonDescargar.addEventListener('click', descargar);
    }

    render();
  });
})();
